import { defineStore } from "pinia";
import { DataConfig, useAdditionalAttrStore } from "./AdditionalAttrStore";
import { useConfiguratorStore } from "./configuratorData";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useLoginStore } from "./LoginStore";
import { openFullLoading } from "@/utils/tools";
import { getFreeTexts } from "@/api/obj.api";
import {
  BackDataType,
  BuildIdCreateResponse,
  BuildIdValuesResponse,
  ConfiguratorIdState,
  CurrentCoding,
  InitialConfig,
} from "./types";
import { ElNotification } from "element-plus";
// import { useI18n } from "vue-i18n";
import i18n from "@/lang";
// import { ElNotification } from "element-plus";

export const configuratorIdStore = defineStore("configuratorIdStore", {
  state: (): ConfiguratorIdState => ({
    ids: [], // codes
    values: [], // codes mapping data.
    res: [], // response data.
    freeTexts: [], // if users can enter some text in criterias by his own, these texts should be here with the criteriaIdx.
    condition: {
      customerNumber: "", // from data config
      environment: "", // from data config
      original_url: "", // from data html, if not set use "window.localtion.origin"
      region: "",
      language: "", // from data config,
      qrcode: {
        mode: "",
        size: 400,
      }, // from data config,
      plannumber: "", // from initConfigurator: "CurrentPlanNo"
      article_number: "", // from initConfigurator: "CurrentType"
      configurations: [], // multi
    },
    backItem: "",
    backData: [],
    priceSync: false,
    tmpConfig: {
      LiveEnvPath: "https://portal.wika.com/app3/de11/portal-prod/",
      buildIdPath: "https://wika.build/api/v1/id/create",
      getBuildIdValues: "https://wika.build/api/v1/id/",
      optionGraphicSettings:
        "https://portal.wika.com/app3/global/configurator/gfx/",
      mailSupport: "info@wika.de",
      hotlinePhone: "0800 123 400 500",
      qrURI: "https://wika.build/qr/",
      qrIdURI: "https://wika.build/id/",
      company: "WIKA group",
      welcomeMessage: "",
      cadenasBaseUrl: "https://webapi.qa.partcommunity.com",
    },
  }),
  getters: {},
  actions: {
    setInitConfiguratorData(
      CurrentPlanNo: string,
      CurrentType: string,
      CodeTypeId: string,
      ContentString: string
    ) {
      this.condition.plannumber = CurrentPlanNo;
      this.condition.article_number = CurrentType;
      this.condition.configurations = [];
      this.condition.configurations.push({
        modelcode_type: CodeTypeId,
        modelcode: ContentString,
        freeTexts: [],
      });
    },
    /**
     * init application set data config
     * @param dataConfig
     */
    setConditionData(dataConfig: DataConfig) {
      if (
        typeof dataConfig.customerNumber &&
        dataConfig.customerNumber !== ""
      ) {
        this.condition.customerNumber = dataConfig.customerNumber;
      }
      //
      if (
        typeof dataConfig.environment !== "undefined" &&
        dataConfig.environment !== ""
      ) {
        this.condition.environment = dataConfig.environment;
      }
      //
      this.condition.original_url =
        dataConfig.original_url !== ""
          ? dataConfig.original_url
          : window.location.origin;
      //
      if (
        typeof dataConfig.language !== "undefined" &&
        dataConfig.language !== ""
      ) {
        this.condition.language = dataConfig.language;
      }
      //
      if (
        typeof dataConfig.qrcode === "object" &&
        dataConfig.qrcode.mode !== ""
      )
        this.condition.qrcode = dataConfig.qrcode;
      //
      this.backData = [];
    },
    /**
     * init configuratorCoding,
     * @param currentCoding
     */
    initConfiguratorCoding(currentCoding: CurrentCoding[]) {
      if (currentCoding.length) {
        this.condition.configurations = [];
        currentCoding.forEach((cc) => {
          if (typeof cc.Optional !== "undefined" && cc.Optional == "No") {
            this.condition.configurations.push({
              modelcode_type: cc.CodeTypeId,
              modelcode: cc.ContentString,
            });
          }
        });
      }
    },
    /**
     * set free text []
     * @param criteriaId
     * @param text
     * @returns
     */
    setFreeText(criteriaId: string, text: string) {
      const freeText = {
        criterion: criteriaId,
        text: text,
      };
      this.checkAndRemoveFreeText(criteriaId);
      if (text !== "") {
        this.freeTexts.push(freeText);
      }
      return this.freeTexts;
    },

    /**
     * check and remove empty item.
     * @param criteriaId
     */
    checkAndRemoveFreeText(criteriaId: string) {
      if (this.freeTexts.length) {
        this.freeTexts = this.freeTexts.filter(
          (obj) => obj.criterion != criteriaId
        );
      }
    },

    canCreateId() {
      let valid = true;
      try {
        const values = Object.values(this.condition);
        Object.getOwnPropertyNames(this.condition).forEach((key, index) => {
          const value = values[index];
          if (typeof value === "object") {
            if (key === "configurations") {
              if ("length" in value && value.length < 1) {
                throw new Error("Not null");
              }
            }
          } else {
            if (!value) {
              throw new Error("Not null");
            }
          }
        });
      } catch (e) {
        valid = false;
      }
      return valid;
    },

    getSelectedProductForCode() {
      const configuratorStore = useConfiguratorStore();
      return configuratorStore.apiConfiguratorResult?.ConfiguratorResult.CurrentState.CurrentCoding?.filter(
        (item) =>
          // isProductAssembly(item) &&
          item.Optional !== "Yes" ||
          configuratorStore.selectedAssemblies
            .map((assembly) => assembly.codeTypeId)
            .includes(item.CodeTypeId) ||
          configuratorStore.getSelectedCertificates.includes(item.CodeTypeId)
      );
    },

    async getConfiguratorId() {
      return await this.createBuildId();
    },
    /**
     * create build id.
     * @see https://wika.build/api/docs#endpoints-POSTapi-v1-id-create
     * @returns
     */
    async createBuildId(): Promise<
      AxiosResponse<BuildIdCreateResponse> | string
    > {
      const userStore = useLoginStore();
      if (this.canCreateId() && userStore.getwebsession) {
        const loading = openFullLoading(false, i18n.global.t("text.loading"));
        const cond = this.condition;
        const additional = useAdditionalAttrStore();

        const selectedProducts = this.getSelectedProductForCode();
        if (selectedProducts?.length) {
          cond.configurations = [];
          selectedProducts.forEach((coding: CurrentCoding) => {
            cond.configurations.push({
              modelcode: coding.ContentString,
              modelcode_type: coding.CodeTypeId,
            });
          });
        }

        const configuratorStore = useConfiguratorStore();
        const resultData = configuratorStore.getResultData;
        const resFreeText = await getFreeTexts();
        if (
          typeof resFreeText.FreeTexts !== "undefined" &&
          resFreeText.FreeTexts.length
        ) {
          resFreeText.FreeTexts.forEach((freeText) => {
            cond.configurations.forEach((configuration, k) => {
              if (configuration.modelcode_type === freeText.CodeTypeId) {
                if (typeof cond.configurations[k].freeTexts === "undefined") {
                  cond.configurations[k].freeTexts = [];
                }

                const CriteriaObjectToinsert = {
                  criterion: freeText.CriteriaId,
                  text: freeText.Text,
                };

                cond.configurations[k].freeTexts = cond.configurations[
                  k
                ].freeTexts?.filter((obj) => obj == CriteriaObjectToinsert);

                cond.configurations[k].freeTexts?.push({
                  criterion: freeText.CriteriaId,
                  text: freeText.Text,
                });
              }
            });
          });
        }
        const postData = {
          plannumber: cond.plannumber,
          article_number: resultData?.ItemInformation?.Item.ItemId ?? "",
          customer_number: cond.customerNumber,
          user_identifier: additional.getLoginName,
          environment: cond.environment,
          erp_tenant: userStore.getTenant,
          original_url: cond.original_url,
          region: cond.region,
          language: additional.getLangApi,
          ConfigurationComplete: configuratorStore.getConfiguratorResult
            ?.CurrentState.CodeComplete
            ? "y"
            : "n",
          is_complete:
            configuratorStore.getConfiguratorResult?.CurrentState.CodeComplete,
          qrcode: {
            mode: cond.qrcode.mode,
            size: cond.qrcode.size,
          },
          configurations: cond.configurations,
        };

        try {
          const response = await axios.post<BuildIdCreateResponse>(
            additional.config.buildIdPath,
            postData,
            {
              timeout: 30 * 1000,
            }
          );
          if (response.status !== 400) {
            this.ids.push(response.data.code);
            this.values.push(this.condition);
            this.res.push(response);
          }
          loading.close();
          return response;
        } catch (error) {
          if (error instanceof AxiosError) {
            ElNotification({
              title: "Unable to Create",
              message: error.message,
              type: "error",
            });
          }
          return "error";
        } finally {
          loading.close();
        }
      }
      return "error.hotlineNotice";
    },
    setBackItem(itemId: string) {
      this.backItem = itemId;
    },
    setBackData(data: BackDataType[]) {
      this.backData = data;
    },
    setPriceSync(flag: boolean) {
      this.priceSync = flag;
    },
    clearBackData() {
      this.backData = [];
      this.backItem = "";
    },
    setRegion(region: string) {
      this.condition.region = region;
    },
    /**
     * get Configurator by Code
     * @see https://wika.build/api/docs#endpoints-GETapi-v1-id--buildCode_code-
     * @param code
     * @returns
     */
    async getConfiguratorByCode(code: string, closeLoading: boolean) {
      const loading = openFullLoading(false, i18n.global.t("text.loading"));
      try {
        const additional = useAdditionalAttrStore();

        const response: AxiosResponse<BuildIdValuesResponse> =
          await axios.get<BuildIdValuesResponse>(
            additional.config.getBuildIdValues + code
          );
        if (response.data?.configurations) {
          this.backData = response.data.configurations;
        }
        return response;
      } catch (error) {
        if (error instanceof AxiosError) {
          return loading.close();
        }

        return {
          status: 400,
          data: { configurations: [], article_number: "" },
        };

        // ElNotification({
        //   title: "Invalid Code",
        //   message: "Please enter valid build code",
        //   type: "warning",
        // });
        // throw new Error("error.codeError");
      } finally {
        if (closeLoading) {
          loading.close();
        }
      }
    },
    setTmpConfig(config: InitialConfig) {
      if (config["config"]) {
        this.tmpConfig = Object.assign({}, this.tmpConfig, config["config"]);
      }
    },
  },
});
